/**
 * Each person's own Google Drive switch for the agent they talk to.
 *
 * The agent's editor ALLOWS Drive (Connectors tab); whether Drive is on is the
 * person's own choice, per agent, made from the conversation's connectors
 * panel — OFF by default — or "automatically for agents that allow it" from
 * Settings → Cloud Storage. The server decides which turns carry the Drive
 * tools (`agent_drive_switch.py`); this file only reads and records the
 * choice.
 *
 * One answer per agent is shared by every composer on the page (sibling
 * branch panels talk to the same agent), and dropped when the person's Google
 * connection changes or the automatic preference is switched.
 */

import api from '@/services/api';
import { authService } from '@modules/auth';
import { subscribeGoogleAccess } from './googleAccessService';

export type DriveSwitchSource = 'chosen' | 'auto' | 'default';

export type AgentDriveSwitch =
  | { allowed: false }
  | {
      allowed: true;
      /** The person's own switch for this agent (chosen, or automatic). */
      on: boolean;
      source: DriveSwitchSource;
      /** On AND connected: their next turn carries Drive. */
      active: boolean;
      connected: boolean;
      account_email: string | null;
      /** What the agent may do with Drive. */
      level: 'save' | 'edit';
    };

const BASE = '/cloud-storage/google_drive';

// Keyed by person and organization as well as agent: a switch is the
// person's own, and one tab may sign in as someone else without a reload.
const cache = new Map<string, AgentDriveSwitch>();
const keyOf = (chatbotId: string) =>
  `${authService.getUser()?.user_id ?? ''}:${authService.getTenantId() ?? ''}:${chatbotId}`;
const inflight = new Map<string, Promise<AgentDriveSwitch>>();
const listeners = new Set<() => void>();
// Bumped by every write: a read that started before a write must not put its
// older answer over the write's (a reconnect refetches while the switch saves).
let generation = 0;

function notify() {
  listeners.forEach((fn) => fn());
}

/** Forget every agent's answer (the connection or the preference changed). */
export function forgetAgentDriveSwitches(): void {
  generation += 1;
  cache.clear();
  inflight.clear();
  notify();
}

/** Forget one agent's answer (a write was refused: the panel was stale). */
export function forgetAgentDriveSwitch(chatbotId: string): void {
  const key = keyOf(chatbotId);
  generation += 1;
  cache.delete(key);
  inflight.delete(key);
  notify();
}

subscribeGoogleAccess(forgetAgentDriveSwitches);

export function subscribeAgentDriveSwitches(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function cachedAgentDriveSwitch(chatbotId: string): AgentDriveSwitch | undefined {
  return cache.get(keyOf(chatbotId));
}

/** This person's switch for this agent. `conversationId` is how a project
 *  member reaches the project thread's agent when not directly. */
export function getAgentDriveSwitch(chatbotId: string, conversationId?: string): Promise<AgentDriveSwitch> {
  const key = keyOf(chatbotId);
  const hit = cache.get(key);
  if (hit) return Promise.resolve(hit);
  let pending = inflight.get(key);
  if (!pending) {
    const startedAt = generation;
    const request: Promise<AgentDriveSwitch> = api
      .get(`${BASE}/agents/${encodeURIComponent(chatbotId)}/switch`,
        { params: conversationId ? { conversation_id: conversationId } : {} })
      .then((res) => {
        const state = res.data.google_drive as AgentDriveSwitch;
        if (startedAt !== generation) return cache.get(key) ?? state;
        cache.set(key, state);
        return state;
      })
      .finally(() => { if (inflight.get(key) === request) inflight.delete(key); });
    inflight.set(key, request);
    pending = request;
  }
  return pending;
}

export async function setAgentDriveSwitch(
  chatbotId: string, enabled: boolean, conversationId?: string,
): Promise<AgentDriveSwitch> {
  const key = keyOf(chatbotId);
  generation += 1;
  inflight.delete(key);  // a read started before this write must not be handed out
  const res = await api.put(`${BASE}/agents/${encodeURIComponent(chatbotId)}/switch`,
    { enabled, ...(conversationId ? { conversation_id: conversationId } : {}) });
  const state = res.data.google_drive as AgentDriveSwitch;
  cache.set(key, state);
  notify();
  return state;
}

export async function getDriveAutoOn(): Promise<boolean> {
  const res = await api.get(`${BASE}/auto-on`);
  return !!res.data.enabled;
}

export async function setDriveAutoOn(enabled: boolean): Promise<boolean> {
  const res = await api.put(`${BASE}/auto-on`, { enabled });
  // Every agent with no choice of its own follows the preference.
  forgetAgentDriveSwitches();
  return !!res.data.enabled;
}
